import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { memoize } from 'lodash';
import * as React from 'react';

import { Bem } from '../../../service/Bem/Bem';
import {
    cloudinessWeatherTitleByType,
    getDefaultValueWeatherRateCorrectionConditionByType,
    getTitleWeatherError,
    rainfallWeatherTitleByType
} from '../../../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import {
    CloudinessTypes,
    EditorWeatherRateCorrectionCondition,
    EditorWeatherRateCorrectionConditionTemperature,
    RainfallTypes,
    RateControlValue,
    RateCorrectionConditionChooserItemsGroup,
    RateCorrectionConditionId,
    RateCorrectionType,
    WeatherRateCorrectionsType
} from '../../../typings/ratesCorrections';
import { HoverTooltipWrapper, HoverTooltipWrapperTheme } from '../../HoverTooltipWrapper/HoverTooltipWrapper';
import { NumberInput } from '../../NumberInput/NumberInput';
import { RateControl } from '../../RateControl/RateControl';
import { RateCorrectionConditionChooserView } from '../../RateCorrectionConditionChooser/RateCorrectionConditionChooser';
import { RateCorrectionConditionSelector } from '../../RateCorrectionConditionSelector/RateCorrectionConditionSelector';

import './RatesCorrectionsListWeatherCondition.css';
import * as keyset from './RatesCorrectionsListWeatherCondition.i18n';

const i18n = i18nFactory(keyset);

export type onChangeWeatherConditionParams = {
    type: RateCorrectionType.weather;
    value: EditorWeatherRateCorrectionCondition;
    id: RateCorrectionConditionId;
};

export interface RatesCorrectionsListWeatherConditionProps {
    value: EditorWeatherRateCorrectionCondition;
    maxRate?: number;
    minRate?: number;
    minTemperatureLimit?: number;
    maxTemperatureLimit?: number;
    chooserData: Array<RateCorrectionConditionChooserItemsGroup>;
    disabledChooserDataItems: Array<string>;
    isHiddenRateControl?: boolean;
    onChange: (params: onChangeWeatherConditionParams) => void;
}

const blockClass = 'rates-corrections-list-weather-condition';

export class RatesCorrectionsListWeatherCondition extends React.PureComponent<
    RatesCorrectionsListWeatherConditionProps
> {
    render() {
        const { chooserData, minRate, maxRate, value, disabledChooserDataItems, isHiddenRateControl } = this.props;

        const titleIntersectionError = getTitleWeatherError(value, 'intersectionItem');
        const weatherType = value.weatherType;

        return (
            <div className={blockClass}>
                <RateCorrectionConditionSelector
                    view={RateCorrectionConditionChooserView.default}
                    cls={classNames(Bem.cls(blockClass, 'selector'), Bem.cls(blockClass, weatherType))}
                    value={[weatherType]}
                    items={chooserData}
                    disabledItems={disabledChooserDataItems}
                    onChange={this.onRateCorrectionConditionChooserChange}
                    valid={titleIntersectionError === undefined}
                />

                {this.renderContentForCurrentWeatherType()}

                {!isHiddenRateControl && (
                    <RateControl
                        cls={Bem.cls(blockClass, 'rate', {
                            temperature: weatherType === WeatherRateCorrectionsType.temperature
                        })}
                        value={value.value}
                        max={maxRate}
                        min={minRate}
                        isCommon={false}
                        errors={value.errors.value}
                        onChange={this.onChangeRateControl}
                    />
                )}
            </div>
        );
    }

    private renderContentForCurrentWeatherType = () => {
        const { value } = this.props;

        const titleIntersectionError = getTitleWeatherError(value, 'intersectionItem');

        switch (value.weatherType) {
            case WeatherRateCorrectionsType.rainfall:
                const { rainfallIds } = value;

                return (
                    <RateCorrectionConditionSelector
                        cls={Bem.cls(blockClass, 'selector')}
                        view={RateCorrectionConditionChooserView.withCheckers}
                        value={rainfallIds}
                        items={getWeatherRainfallChooserData(rainfallIds)}
                        disabledItems={getWeatherRainfallDisabledItems(rainfallIds)}
                        onChange={this.onChangeWeatherRainfallCondition}
                        multiSelect={true}
                        valid={titleIntersectionError === undefined}
                    />
                );
            case WeatherRateCorrectionsType.cloudiness:
                const { cloudinessIds } = value;

                return (
                    <RateCorrectionConditionSelector
                        cls={Bem.cls(blockClass, 'selector')}
                        view={RateCorrectionConditionChooserView.withCheckers}
                        value={cloudinessIds}
                        items={getWeatherCloudinessChooserData(cloudinessIds)}
                        disabledItems={getWeatherCloudinessDisabledItems(cloudinessIds)}
                        onChange={this.onChangeWeatherCloudinessCondition}
                        multiSelect={true}
                        valid={titleIntersectionError === undefined}
                    />
                );
            case WeatherRateCorrectionsType.temperature:
                return this.renderTemperatureBlock(value);
            default:
                throw new Error(`Неподдерживаемый тип корректировок ставок на погоду`);
        }
    };

    private renderTemperatureBlock = (value: EditorWeatherRateCorrectionConditionTemperature) => {
        const titleIntersectionError = getTitleWeatherError(value, 'intersectionItem');
        const titleMaxTemperatureError = getTitleWeatherError(value, 'maxTemperature');

        return (
            <>
                <span className={Bem.cls(blockClass, 'from')}>{i18n('from')}</span>
                <NumberInput
                    cls={Bem.cls(blockClass, 'min-temperature-value')}
                    fractionalPartLength={0}
                    theme="normal"
                    size="s"
                    view="classic"
                    value={value.minTemperature}
                    onValueChange={this.onChangeMinTemperature}
                    valid={titleIntersectionError === undefined}
                />
                <span className={Bem.cls(blockClass, 'to')}>{i18n('to')}</span>
                <HoverTooltipWrapper
                    cls={Bem.cls(blockClass, 'wrapper-tooltip')}
                    theme={'error'}
                    themeWrapper={HoverTooltipWrapperTheme.clear}
                    view={'classic'}
                    to={['right']}
                    size={'s'}
                    tooltipText={titleMaxTemperatureError}
                >
                    <NumberInput
                        cls={Bem.cls(blockClass, 'max-temperature-value')}
                        fractionalPartLength={0}
                        theme="normal"
                        size="s"
                        view="classic"
                        value={value.maxTemperature}
                        onValueChange={this.onChangeMaxTemperature}
                        valid={titleIntersectionError === undefined && titleMaxTemperatureError === undefined}
                    />
                </HoverTooltipWrapper>
                <span className={Bem.cls(blockClass, 'degree')}>{i18n('degree')}</span>
            </>
        );
    };

    private onRateCorrectionConditionChooserChange = (listSelectedItem: Array<string>) => {
        const { onChange, value, minTemperatureLimit, maxTemperatureLimit } = this.props;
        const selectedItem = listSelectedItem[0];

        if (selectedItem) {
            onChange({
                type: RateCorrectionType.weather,
                id: value.id,
                value: {
                    ...getDefaultValueWeatherRateCorrectionConditionByType(selectedItem as WeatherRateCorrectionsType, {
                        minTemperature: minTemperatureLimit,
                        maxTemperature: maxTemperatureLimit
                    }),
                    value: value.value
                }
            });
        }
    };

    private onChangeRateControl = (rateControlValue: RateControlValue) => {
        const { onChange, value } = this.props;

        onChange({
            type: RateCorrectionType.weather,
            id: value.id,
            value: {
                ...value,
                value: rateControlValue
            }
        });
    };

    private onChangeWeatherRainfallCondition = (values: Array<string>) => {
        const { onChange, value } = this.props;

        if (value.weatherType === WeatherRateCorrectionsType.rainfall) {
            onChange({
                type: RateCorrectionType.weather,
                id: value.id,
                value: {
                    ...value,
                    rainfallIds: values as Array<RainfallTypes>
                }
            });
        }
    };

    private onChangeWeatherCloudinessCondition = (values: Array<string>) => {
        const { onChange, value } = this.props;

        if (value.weatherType === WeatherRateCorrectionsType.cloudiness) {
            onChange({
                type: RateCorrectionType.weather,
                id: value.id,
                value: {
                    ...value,
                    cloudinessIds: values as Array<CloudinessTypes>
                }
            });
        }
    };

    private onChangeMinTemperature = (minTemperature: number) => {
        const { onChange, value } = this.props;

        if (value.weatherType === WeatherRateCorrectionsType.temperature) {
            onChange({
                type: RateCorrectionType.weather,
                id: value.id,
                value: {
                    ...value,
                    minTemperature
                }
            });
        }
    };

    private onChangeMaxTemperature = (maxTemperature: number) => {
        const { onChange, value } = this.props;

        if (value.weatherType === WeatherRateCorrectionsType.temperature) {
            onChange({
                type: RateCorrectionType.weather,
                id: value.id,
                value: {
                    ...value,
                    maxTemperature
                }
            });
        }
    };
}

const getWeatherRainfallDisabledItems = memoize((rainfallIds: RainfallTypes[]) => {
    return rainfallIds.length === 1 ? rainfallIds : [];
});

const getWeatherCloudinessDisabledItems = memoize((cloudinessIds: CloudinessTypes[]) => {
    return cloudinessIds.length === 1 ? cloudinessIds : [];
});

const getWeatherRainfallChooserData = memoize((rainfallIds: RainfallTypes[]) => {
    return [
        {
            hasMultiChoice: true,
            items: Object.keys(rainfallWeatherTitleByType).map(key => {
                const hasWarning = rainfallIds.includes(key as RainfallTypes) && rainfallIds.length === 1;

                return {
                    id: key,
                    name: rainfallWeatherTitleByType[key],
                    hint: hasWarning ? i18n('warning-one-more-item') : undefined
                };
            })
        }
    ];
});

const getWeatherCloudinessChooserData = memoize((cloudinessIds: CloudinessTypes[]) => {
    return [
        {
            hasMultiChoice: true,
            items: Object.keys(cloudinessWeatherTitleByType).map(key => {
                const hasWarning = cloudinessIds.includes(key as CloudinessTypes) && cloudinessIds.length === 1;

                return {
                    id: key,
                    name: cloudinessWeatherTitleByType[key],
                    hint: hasWarning ? i18n('warning-one-more-item') : undefined
                };
            })
        }
    ];
});
